// Sisense to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const sisensePage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Sisense to Amazon Quick',
    intro: 'The reasons we hear most from Sisense customers running on AWS.',
    cards: [
      { title: 'No more ElastiCube servers', description: 'Cube builds move to Redshift or SPICE, so there are no separate Sisense servers to size, patch, and rebuild.' },
      { title: 'Embedded analytics on AWS', description: 'Customer-facing dashboards move to Quick Sight embedding, with the same per-tenant security.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Sisense metadata',
    title: 'We read your Sisense instance through its APIs',
    intro: 'Instead of weeks of interviews, we pull metadata straight from Sisense. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'ElastiCube and live models: tables, joins, custom columns', from: 'Data model REST API', use: 'Size the data landing in Amazon Redshift or SPICE and rebuild the joins as datasets' },
      { what: 'Build schedules and row counts', from: 'Data model REST API', use: 'Plan refresh pipelines and SPICE ingestion' },
      { what: 'Dashboards and widgets', from: 'Dashboards REST API', use: 'Rebuild visuals and spot duplicate dashboards' },
      { what: 'Widget formulas and filters', from: 'Dashboard definitions', use: 'Convert Sisense formulas to calculated fields and filters to controls' },
      { what: 'Data security rules', from: 'Data security API', use: 'Recreate the same row-level security on Amazon Quick' },
      { what: 'Users, groups, and usage', from: 'Users and Groups APIs, Usage Analytics', use: 'Find the most-used and unused dashboards to rank waves and plan training by role' },
      { what: 'Embedding setup and plugins', from: 'Embed configuration and plugin list', use: 'Plan the move to Quick Sight embedding and flag custom plugins and BloX to rebuild' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every widget traced back through its ElastiCube to the source system.' },
        { title: 'Complexity score per dashboard', description: 'Formulas, plugins, and data security rules scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by usage analytics, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Sisense through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Sisense admin creates an API token for a read-only service user. Nothing in Sisense changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Sisense migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every widget.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled through the Sisense APIs: ElastiCubes, dashboards, widgets, formulas, data security, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Dashboards grouped into waves by usage, shared cubes, and internal vs. embedded audience. Unused dashboards are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'ElastiCubes rebuilt in Redshift or SPICE, formulas converted to calculated fields, and widgets rebuilt as visuals.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated dashboard compared with its Sisense original, including embedded views. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Sisense dashboards today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Sisense dashboards used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
